import { useApolloClient } from '@apollo/client';
import { debounce } from 'lodash';
import { Dispatch, ReactNode, Reducer, useCallback, useMemo, useReducer, useRef } from 'react';
import { Queries } from '@shared/api';
import { CARD_PAGINATION_SIZE } from '@shared/styles';
import { API } from '@shared/types/api';
import { usePersistentRef } from '@shared/util/hooks';
import { Action, State, initialState, reducer } from './reducer';
import { CardPaginationSubscriptions } from './subscriptions';
import { CardPaginationQuery, CardPaginationQueryStateWithCards } from './types';
import {
  getQueryLegacyRelationFilters,
  getQueryOrder,
  getQueryRelationFilters,
  getQueryStartDateAfter,
  getQueryStartDateBefore,
  toKey,
} from './util';
import {
  ActionsContext,
  CardPaginationActionsContextProvider,
  CardPaginationCreatorOrAssigneeFilter,
  CardPaginationQueryState,
  CardPaginationStateContextProvider,
  StateContext,
} from '.';

interface ProviderProps {
  children: ReactNode;
}

function useBatchDispatch(dispatch: Dispatch<Action>): Dispatch<Action> {
  const actions = useRef<Action[]>([]);
  const dispatchAll = useCallback(() => {
    dispatch({ type: 'batch', actions: actions.current });
    actions.current = [];
  }, [dispatch]);

  const debouncedDispatchAll = useMemo(() => debounce(dispatchAll, 100), [dispatchAll]);
  return useCallback(
    (action: Action) => {
      actions.current.push(action);
      debouncedDispatchAll();
    },
    [debouncedDispatchAll],
  );
}

export function CardPaginationProvider({ children }: ProviderProps) {
  const [state, dispatch] = useReducer<Reducer<State, Action>>(reducer, initialState);
  const batchDispatch = useBatchDispatch(dispatch);
  const stateRef = usePersistentRef(state);
  const apolloClient = useApolloClient();

  const fetchMore = useCallback(
    async (query: CardPaginationQuery, reset: boolean = false) => {
      const queryKey = toKey(query);
      const queryState = stateRef.current.stateByQuery[queryKey];
      if (!reset && queryState?.loading) {
        return;
      }
      if (!reset && queryState?.hasMore === false) {
        return;
      }
      try {
        batchDispatch({ type: 'updateSection', query, queryState: { loading: true } });

        const previousCardIds = reset ? [] : queryState?.cardIds || [];

        const { data } = await apolloClient.query<API.ListCardsQuery, API.ListCardsQueryVariables>({
          query: Queries.card.listCards,
          fetchPolicy: 'no-cache',
          variables: {
            input: {
              channelId: query.channelId,
              statuses: query.statuses,
              startDateAfter: getQueryStartDateAfter(query),
              startDateBefore: getQueryStartDateBefore(query),
              limit: CARD_PAGINATION_SIZE,
              offset: previousCardIds.length,
              relationFilters: getQueryRelationFilters(query),
              legacyRelationFilters: getQueryLegacyRelationFilters(query),
              createdByOrAssignedToUserId: query.filters?.find(
                (f): f is CardPaginationCreatorOrAssigneeFilter => f.type === 'creatorOrAssignee',
              )?.userId,
              order: getQueryOrder(query),
            },
          },
        });

        const cards = data?.list.cards ?? [];
        const sectionUpdate: Partial<CardPaginationQueryState> = {
          loaded: true,
          loading: false,
          hasMore: data?.list.hasMore,
          cardIds: [...previousCardIds, ...cards.map(c => c.id)],
        };
        batchDispatch({
          type: 'updateSection',
          query,
          cards: data.list.cards ?? undefined,
          queryState: sectionUpdate,
        });

        return {
          ...queryState,
          ...sectionUpdate,
          cards: queryState?.cardIds.map(id => stateRef.current.cardById[id]),
        };
      } catch (error) {
        console.error(error);
        batchDispatch({ type: 'updateSection', query, queryState: { loading: false } });
      }
    },
    [batchDispatch, apolloClient, stateRef],
  );

  const addCard = useCallback(
    (card: API.CardFragment, batch: boolean = false) =>
      (batch ? batchDispatch : dispatch)({ type: 'addCard', card }),
    [dispatch, batchDispatch],
  );
  const updateCard = useCallback(
    (card: API.CardFragment, batch: boolean = false) =>
      (batch ? batchDispatch : dispatch)({ type: 'updateCard', card }),
    [dispatch, batchDispatch],
  );
  const removeCard = useCallback(
    (cardId: string, batch: boolean = false) =>
      (batch ? batchDispatch : dispatch)({ type: 'removeCard', cardId }),
    [dispatch, batchDispatch],
  );

  const getState = useCallback(
    (query: CardPaginationQuery): CardPaginationQueryStateWithCards | undefined => {
      const queryState = state.stateByQuery[toKey(query)];
      if (!queryState) {
        return undefined;
      }
      return { ...queryState, cards: queryState.cardIds.map(id => state.cardById[id]) };
    },
    [state.stateByQuery, state.cardById],
  );

  const getCard = useCallback((cardId: string) => state.cardById[cardId], [state.cardById]);

  const cardPaginationState: StateContext = useMemo(
    () => ({ state, getState, getCard }),
    [state, getState, getCard],
  );

  const cardPaginationActions: ActionsContext = useMemo(
    () => ({ fetchMore, addCard, updateCard, removeCard }),
    [fetchMore, addCard, updateCard, removeCard],
  );

  return (
    <CardPaginationStateContextProvider value={cardPaginationState}>
      <CardPaginationActionsContextProvider value={cardPaginationActions}>
        {children}
        <CardPaginationSubscriptions />
      </CardPaginationActionsContextProvider>
    </CardPaginationStateContextProvider>
  );
}
